/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  ArrowsUpDownIcon,
  ArrowUturnLeftIcon,
  MapPinIcon,
  StopIcon,
  BoltIcon,
  ChartBarIcon,
  EyeIcon,
  FireIcon,
  HomeIcon,
  LightBulbIcon,
  LockClosedIcon,
  MoonIcon,
  PlayIcon,
  Cog6ToothIcon,
  EllipsisHorizontalIcon,
  SparklesIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  SunIcon,
  VideoCameraIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { getHub, getHubCamera, pressHubDevice, wakeServer, type HubDevice, type HubState } from "@/api/homeassistant";
import { DevicePicker } from "./DevicePicker";
import { VacuumArt } from "./VacuumArt";
import { useHubNotices, type Notice } from "./notices";
import { buzz, HAPTIC, hapticsEnabled, hapticsSupported, setHaptics } from "./haptics";
import { WeatherIcon, classify } from "@/components/common/TopInfo";
import { getOverview, type OverviewCategory } from "@/api/overview";
import { getHistory } from "@/api/speedtest";
import { getWeather } from "@/api/weather";
import { useTabNavigationContext } from "@/context/tabNavigation";
import { getEffectiveTimezone, getTimeFormatSettings } from "@/utils/timeSettings";
import type { PaginatedResponse, SpeedTestResult } from "@/types/types";
import "./hub.css";

type NightMode = "auto" | "on" | "off";
const NIGHT_KEY = "nyrvex-hub-night";
// Quiet hours: between these local hours the display makes no sound (notices still show).
const QUIET_KEY = "nyrvex-hub-quiet";
const QUIET_FROM = 20;
const QUIET_TO = 10;
const NIGHT_FROM = 23;
const NIGHT_TO = 7;

const readNight = (): NightMode => {
  try {
    const v = localStorage.getItem(NIGHT_KEY);
    return v === "on" || v === "off" ? v : "auto";
  } catch {
    return "auto";
  }
};

const DEVICE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  light: LightBulbIcon,
  switch: BoltIcon,
  fan: BoltIcon,
  input_boolean: BoltIcon,
  automation: BoltIcon,
  cover: ArrowsUpDownIcon,
  vacuum: HomeIcon,
  scene: SparklesIcon,
  script: PlayIcon,
  button: PlayIcon,
  input_button: PlayIcon,
  climate: FireIcon,
  sensor: ChartBarIcon,
  binary_sensor: EyeIcon,
  lock: LockClosedIcon,
  camera: VideoCameraIcon,
};

const KIND_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  locate: MapPinIcon,
  home: ArrowUturnLeftIcon,
  stop: StopIcon,
};

function deviceLine(d: HubDevice): string {
  if (d.kind) return ({ locate: "beep", home: "dock", stop: "stop" } as Record<string, string>)[d.kind] ?? "run";
  if (!d.available) return "unavailable";
  if (d.value) return d.value;
  if (d.action === "view") return "snapshot";
  if (d.action === "press") return "run";
  if (d.domain === "cover") return d.on ? "open" : "closed";
  if (d.domain === "binary_sensor") return d.on ? "active" : "clear";
  if (d.domain === "vacuum" && !d.kind) return d.battery !== undefined ? `${d.state} · ${d.battery}%` : d.state;
  if (d.domain === "vacuum") return d.battery !== undefined ? `${d.state} · ${d.battery}%` : d.state;
  if (d.domain === "lock") return d.on ? "unlocked" : "locked";
  return d.on ? (d.brightness ? `on · ${d.brightness}%` : "on") : "off";
}

const TILES: { key: keyof NonNullable<ReturnType<typeof tileData>>; label: string }[] = [
  { key: "routers", label: "Routers" },
  { key: "agents", label: "Servers" },
  { key: "switches", label: "Switches" },
  { key: "services", label: "Services" },
];

function tileData(o?: { categories: Record<string, OverviewCategory> }) {
  return o?.categories as Record<string, OverviewCategory> | undefined;
}

/** A camera snapshot, refreshed every couple of seconds while it is open. */
const CameraOverlay: React.FC<{ device: HubDevice; onClose: () => void }> = ({ device, onClose }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let stop = false;
    let current: string | null = null;
    const load = async () => {
      try {
        const next = await getHubCamera(device.entityId);
        if (stop) {
          URL.revokeObjectURL(next);
          return;
        }
        if (current) URL.revokeObjectURL(current);
        current = next;
        setSrc(next);
        setFailed(false);
      } catch {
        if (!stop) setFailed(true);
      }
    };
    void load();
    const t = window.setInterval(() => void load(), 2000);
    return () => {
      stop = true;
      window.clearInterval(t);
      if (current) URL.revokeObjectURL(current);
    };
  }, [device.entityId]);

  return (
    <div className="hub-cam" onClick={onClose} role="dialog" aria-label={device.name}>
      <div className="hub-cam-box" onClick={(e) => e.stopPropagation()}>
        <div className="hub-cam-head">
          <b>{device.name}</b>
          <button type="button" onClick={onClose} aria-label="Close">
            <XMarkIcon />
          </button>
        </div>
        {src && <img src={src} alt={device.name} />}
        {failed && <p className="hub-err">No picture</p>}
      </div>
    </div>
  );
};

/**
 * A wall-display for an old phone or tablet: big clock and weather, one
 * glanceable health verdict, the numbers behind it and the latest speed test.
 * It dims itself at night, goes loud (red) when something is critical, keeps
 * the screen awake and nudges its content around to spare OLED panels.
 */
export const HubPage: React.FC = () => {
  const navigate = useNavigate();
  const { setNav } = useTabNavigationContext();
  const [now, setNow] = useState(() => new Date());
  const [night, setNight] = useState<NightMode>(readNight);
  const [menu, setMenu] = useState(false);
  const [quietHours, setQuietHours] = useState<boolean>(() => {
    try {
      return localStorage.getItem(QUIET_KEY) !== "off";
    } catch {
      return true;
    }
  });
  const [haptics, setHapticsState] = useState(hapticsEnabled);
  const [fullscreen, setFullscreen] = useState(() => !!document.fullscreenElement);
  const [picking, setPicking] = useState(false);
  const [camera, setCamera] = useState<HubDevice | null>(null);
  const [waking, setWaking] = useState<number | null>(null);
  const queryClient = useQueryClient();


  // This page owns the whole screen: no sidebar, no tab bar.
  useEffect(() => {
    setNav({ tabs: [], activeTab: "", onTabChange: () => {}, visible: false });
  }, [setNav]);

  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(t);
  }, []);

  // Keep the screen on while the page is visible.
  useEffect(() => {
    let lock: WakeLockSentinel | null = null;
    const acquire = async () => {
      try {
        if (document.visibilityState === "visible" && "wakeLock" in navigator) {
          lock = await navigator.wakeLock.request("screen");
        }
      } catch {
        /* denied (battery saver) -- the display just follows the OS timeout */
      }
    };
    void acquire();
    document.addEventListener("visibilitychange", acquire);
    return () => {
      document.removeEventListener("visibilitychange", acquire);
      void lock?.release().catch(() => undefined);
    };
  }, []);

  useEffect(() => {
    const onFs = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const { data: overview, isError } = useQuery({ queryKey: ["hub-overview"], queryFn: getOverview, refetchInterval: 10_000, retry: false });
  const { data: weather } = useQuery({ queryKey: ["weather"], queryFn: getWeather, refetchInterval: 15 * 60_000, retry: false });
  const { data: speed } = useQuery({
    queryKey: ["hub-speed"],
    queryFn: () => getHistory("all", 1, 10, "local") as Promise<PaginatedResponse<SpeedTestResult>>,
    refetchInterval: 5 * 60_000,
    retry: false,
  });

  const { data: hub } = useQuery({ queryKey: ["hub-ha"], queryFn: getHub, refetchInterval: 5_000, retry: false });
  const devices = hub?.devices ?? [];
  const people = hub?.people ?? [];
  const wakeable = hub?.wake ?? [];
  // Read-only values (battery, temperature…) become a quiet row of chips; only things you can tap are buttons.
  // The vacuum's battery lives on its own tile, so its separate battery sensor is not repeated as a chip.
  const vacPrefixes = devices.filter((d) => d.domain === "vacuum" && !d.kind).map((d) => `sensor.${d.entityId.slice(7)}_battery`);
  const readouts = devices.filter((d) => d.action === "none" && !vacPrefixes.some((p) => d.entityId.startsWith(p)));
  // The vacuum has a page of its own now (start, intensity, water, dock, find),
  // so its extra one-shot buttons no longer crowd the grid.
  const buttons = devices.filter((d) => d.action !== "none" && !(d.domain === "vacuum" && d.kind));

  // Tap: flip the button at once, let Home Assistant confirm on the next poll.
  const press = async (d: HubDevice) => {
    if (d.action === "none" || !d.available) return;
    buzz(HAPTIC.tap);
    if (d.action === "view") {
      setCamera(d);
      return;
    }
    if (d.domain === "vacuum" && !d.kind) {
      void navigate({ to: "/hub", search: { page: "vacuum" } });
      return;
    }
    queryClient.setQueryData<HubState>(["hub-ha"], (old) =>
      old ? { ...old, devices: old.devices.map((x) => (x.entityId === d.entityId && d.action === "toggle" ? { ...x, on: !x.on } : x)) } : old,
    );
    try {
      await pressHubDevice(d.entityId);
      buzz(HAPTIC.ok);
    } catch {
      buzz(HAPTIC.fail); // and the refetch below restores the real state
    }
    window.setTimeout(() => void queryClient.invalidateQueries({ queryKey: ["hub-ha"] }), 700);
  };

  const wake = async (id: number) => {
    buzz(HAPTIC.tap);
    setWaking(id);
    try {
      await wakeServer(id);
      buzz(HAPTIC.ok);
    } catch {
      buzz(HAPTIC.fail);
    }
    window.setTimeout(() => setWaking(null), 20_000);
  };

  const tz = getEffectiveTimezone(getTimeFormatSettings());
  const parts = (opts: Intl.DateTimeFormatOptions) => now.toLocaleString(undefined, { ...opts, timeZone: tz });
  const hour = Number(parts({ hour: "numeric", hour12: false })) % 24;
  const isNightHours = hour >= NIGHT_FROM || hour < NIGHT_TO;
  const dim = night === "on" || (night === "auto" && isNightHours);

  const problems = useMemo(
    () => [...(overview?.problems ?? [])].sort((a, b) => Number(b.severity === "crit") - Number(a.severity === "crit")),
    [overview],
  );
  const crit = problems.filter((p) => p.severity === "crit").length;
  const state: "ok" | "warn" | "crit" | "off" = isError || !overview ? "off" : crit > 0 ? "crit" : problems.length > 0 ? "warn" : "ok";
  // A critical failure overrides night dimming: waking the screen is the point.
  const dimmed = dim && state !== "crit";


  const inQuietHours = quietHours && (hour >= QUIET_FROM || hour < QUIET_TO);
  const { notices, dismiss, sound, setSound, unlock, audioReady } = useHubNotices(overview?.problems, devices, dimmed, inQuietHours);

  // Nudge the content a few pixels every minute (OLED burn-in).
  const shift = useMemo(() => {
    const m = Math.floor(now.getTime() / 60_000);
    return { x: ((m * 7) % 9) - 4, y: ((m * 5) % 7) - 3 };
  }, [now]);

  // Tap outside the menu closes it.
  const closeMenu = useCallback((e: React.PointerEvent) => {
    unlock();
    if (!(e.target as HTMLElement).closest(".hub-menu, .hub-more-btn")) setMenu(false);
  }, [unlock]);

  const cycleNight = () => {
    const next: NightMode = night === "auto" ? "on" : night === "on" ? "off" : "auto";
    setNight(next);
    try {
      localStorage.setItem(NIGHT_KEY, next);
    } catch {
      /* ignore */
    }
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen?.().catch(() => undefined);
  };

  const cats = tileData(overview);
  const latest = speed?.data?.find((r) => r.downloadSpeed > 0);
  const hm = parts({ hour: "2-digit", minute: "2-digit", hour12: false });
  const ss = parts({ second: "2-digit" }).padStart(2, "0");
  const date = parts({ weekday: "long", day: "numeric", month: "long" });
  const deg = weather?.units === "fahrenheit" ? "°F" : "°C";
  const showDevices = !!hub?.configured;
  // Only categories with a problem get a colour; the rest stay a quiet reference line.
  const footCats = TILES.map((t) => ({ label: t.label, c: cats?.[t.key as string] })).filter((x) => x.c);

  return (
    <div className={`hub hub--${state}${dimmed ? " hub--dim" : ""}${showDevices ? " hub--devices" : " hub--nodev"}`} onPointerDown={closeMenu} role="main">
      <div className="hub-inner" style={{ transform: `translate(${shift.x}px, ${shift.y}px)` }}>
        <div className="hub-left">
          <section className="hub-clock" aria-label="Time">
            <div className="hub-time">
              {hm}
              {!dimmed && <span>:{ss}</span>}
            </div>
            <div className="hub-date">{date}</div>
            {weather && (
              <div className="hub-weather">
                <WeatherIcon kind={classify(weather.weatherCode).kind} className="hub-wx" />
                <b>
                  {Math.round(weather.temperature)}
                  {deg}
                </b>
                <span>{weather.condition}</span>
                {weather.daily?.[0] && (
                  <span className="hub-range">{`${Math.round(weather.daily[0].tempMin)}…${Math.round(weather.daily[0].tempMax)}${deg}`}</span>
                )}
              </div>
            )}
            {people.length > 0 && (
              <div className="hub-people">
                {people.map((p) => (
                  <span key={p.name} className={p.home ? "hub-people--home" : ""}>
                    {p.name}
                    <i>{p.home ? "home" : p.state === "not_home" ? "away" : p.state}</i>
                  </span>
                ))}
              </div>
            )}
          </section>
        </div>

        {showDevices ? (
          <section className="hub-devices" aria-label="Home">
            {readouts.length > 0 && (
              <div className="hub-reads">
                {readouts.map((d) => (
                  <span key={d.entityId} className={d.available ? "" : "hub-read--off"}>
                    {d.name}
                    <b>{d.value ?? d.state}</b>
                  </span>
                ))}
              </div>
            )}
            <div className="hub-buttons">
              {buttons.map((d, i) => {
                const primary = d.domain === "vacuum" && !d.kind;
                if (primary) {
                  return (
                    <button
                      key={d.entityId}
                      type="button"
                      className={`hub-vac-tile${d.on ? " hub-vac-tile--on" : ""}${d.available ? "" : " hub-dev--off"}`}
                      onClick={() => void press(d)}
                      disabled={!d.available}
                      aria-label={d.name}
                      style={{ "--i": i } as React.CSSProperties}
                    >
                      <VacuumArt photo={hub?.vacuumPhoto} cleaning={d.on} />
                      {d.battery !== undefined && (
                        <span className="hub-vac-bat" aria-label="Battery">
                          <b>{`${d.battery}%`}</b>
                          <i style={{ "--lvl": `${d.battery}%` } as React.CSSProperties} className={d.battery < 20 ? "low" : ""} />
                        </span>
                      )}
                    </button>
                  );
                }
                const Icon = primary ? HomeIcon : (d.kind && KIND_ICON[d.kind]) || DEVICE_ICON[d.domain] || BoltIcon;
                return (
                  <button
                    key={d.entityId}
                    type="button"
                    className={`hub-dev${primary ? " hub-dev--primary" : ""}${d.on && d.available && (d.action === "toggle" || primary) ? " hub-dev--on" : ""}${d.available ? "" : " hub-dev--off"}`}
                    onClick={() => void press(d)}
                    disabled={!d.available}
                    aria-pressed={d.action === "toggle" ? d.on : undefined}
                    style={{ "--i": i } as React.CSSProperties}
                  >
                    <i className="hub-ico">
                      <Icon />
                    </i>
                    <span className="hub-dev-t">
                      <b>{d.name}</b>
                      <span>{deviceLine(d)}</span>
                    </span>
                  </button>
                );
              })}
              {devices.length === 0 && (
                <button type="button" className="hub-dev hub-dev--add" onClick={() => setPicking(true)}>
                  <i className="hub-ico">
                    <Cog6ToothIcon />
                  </i>
                  <span className="hub-dev-t">
                    <b>Add devices</b>
                    <span>from Home Assistant</span>
                  </span>
                </button>
              )}
            </div>
            {hub?.error && <p className="hub-err">{hub.error}</p>}
          </section>
        ) : null}

        <footer className="hub-foot" aria-label="Fleet">
          {footCats.map(({ label, c }) => (
            <span key={label} className={c && c.total - c.up > 0 ? "hub-bad" : ""}>
              {label}
              <b>
                {c?.up}/{c?.total}
              </b>
            </span>
          ))}
          {wakeable.map((w) => (
            <button key={w.id} type="button" className="hub-wake" disabled={waking === w.id} onClick={() => void wake(w.id)}>
              {waking === w.id ? `Waking ${w.name}…` : `Power on ${w.name}`}
            </button>
          ))}
          {latest && (
            <span className="hub-net">
              Internet
              <b>
                ↓{Math.round(latest.downloadSpeed)} ↑{Math.round(latest.uploadSpeed)} Mbps
              </b>
            </span>
          )}
        </footer>
      </div>

      <div className="hub-notices" aria-live="assertive">
        {notices.map((n: Notice) => (
          <button key={n.id} type="button" className={`hub-notice hub-notice--${n.kind}`} onClick={() => dismiss(n.id)}>
            <em>{n.label}</em>
            <b>{n.name}</b>
            {n.text && <span>{n.text}</span>}
          </button>
        ))}
      </div>
      <button type="button" className="hub-more-btn" onClick={() => setMenu((v) => !v)} aria-label="Menu" aria-expanded={menu}>
        <EllipsisHorizontalIcon />
      </button>
      {menu && (
        <div className="hub-menu" role="menu">
          {hub?.configured && (
            <button type="button" role="menuitem" onClick={() => { setMenu(false); setPicking(true); }}>
              <Cog6ToothIcon />
              <span>Choose devices</span>
            </button>
          )}
          <button type="button" role="menuitem" onClick={cycleNight}>
            {dim ? <MoonIcon /> : <SunIcon />}
            <span>Night mode</span>
            <em>{night}</em>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              const next = !quietHours;
              setQuietHours(next);
              try {
                localStorage.setItem(QUIET_KEY, next ? "on" : "off");
              } catch {
                /* ignore */
              }
            }}
          >
            <MoonIcon />
            <span>Mute sound at night</span>
            <em>{quietHours ? `${QUIET_FROM}:00–${QUIET_TO}:00` : "off"}</em>
          </button>
          <button type="button" role="menuitem" onClick={() => setSound(!sound)}>
            {sound ? <SpeakerWaveIcon /> : <SpeakerXMarkIcon />}
            <span>Sound</span>
            <em>{!sound ? "off" : audioReady ? "on" : "tap once"}</em>
          </button>
          {hapticsSupported() && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                const next = !haptics;
                setHapticsState(next);
                setHaptics(next);
              }}
            >
              <DevicePhoneMobileIcon />
              <span>Vibration</span>
              <em>{haptics ? "on" : "off"}</em>
            </button>
          )}
          <button type="button" role="menuitem" onClick={() => window.location.reload()}>
            <ArrowPathIcon />
            <span>Refresh page</span>
          </button>
          <button type="button" role="menuitem" onClick={toggleFullscreen}>
            {fullscreen ? <ArrowsPointingInIcon /> : <ArrowsPointingOutIcon />}
            <span>Fullscreen</span>
          </button>
          <button type="button" role="menuitem" onClick={() => navigate({ to: "/" })}>
            <XMarkIcon />
            <span>Close hub</span>
          </button>
        </div>
      )}
      {camera && <CameraOverlay device={camera} onClose={() => setCamera(null)} />}
      {picking && <DevicePicker current={devices} onClose={() => setPicking(false)} />}
    </div>
  );
};
